/* ACROSS JAPAN shop (Astra design) — behaviour.
   1. One selected-variant state: Dawn publishes variantChange; the spec table SKU/options and the
      sticky bar (thumbnail, price, availability, loading) follow it.
   2. Mobile sticky buy bar: visible once the main action is above the viewport (checked on init,
      scroll and resize — not only on intersection changes), hidden over overlays and near the
      footer; reserves its measured height; mirrors add-to-cart errors beside its own button.
   3. "Ask about this product" carries product + selected options into the contact form. */
(() => {
  const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());

  // ---------- 1. selected-variant state ----------
  const ja = () => document.documentElement.lang !== 'en';
  const applyVariant = (variant) => {
    // Unresolved/unavailable selection: clear the variant-specific surfaces instead of leaving stale values.
    if (!variant) {
      document.querySelectorAll('[data-ax-sku]').forEach((el) => { el.textContent = '—'; });
      document.querySelectorAll('[data-ax-variant-title]').forEach((el) => { el.textContent = ja() ? '（この組み合わせは選べません）' : '(combination unavailable)'; });
      return;
    }
    document.querySelectorAll('[data-ax-sku]').forEach((el) => { el.textContent = variant.sku || ''; });
    document.querySelectorAll('[data-ax-variant-title]').forEach((el) => { el.textContent = `(${variant.title})`; });
    const src = variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src;
    document.querySelectorAll('aj-sticky-buy').forEach((bar) => {
      const img = bar.querySelector('.aj-sticky__thumb');
      if (!img) return;
      let next = bar.dataset.fallbackThumb || img.getAttribute('src');
      if (src) { const url = new URL(src, window.location.origin); url.searchParams.set('width', '120'); next = url.toString(); }
      if (next && img.getAttribute('src') !== next) { img.src = next; img.removeAttribute('srcset'); }
    });
  };
  // The selected variant as Dawn last rendered it (null when the combination is unavailable).
  const readSelected = (root) => {
    const el = (root || document).querySelector('variant-selects [data-selected-variant]');
    if (!el) return undefined;
    try { return JSON.parse(el.textContent); } catch (e) { return undefined; }
  };
  onReady(() => {
    // Dawn replaces <variant-selects> wholesale on every option change (and skips publish for unavailable
    // combinations), so watch the stable <product-info> root and re-read the rendered selection.
    document.querySelectorAll('product-info').forEach((info) => {
      let last = null;
      let raf = 0;
      const check = () => {
        raf = 0;
        const el = info.querySelector('variant-selects [data-selected-variant]');
        if (!el) return;
        const text = el.textContent.trim();
        if (text === last) return;
        const first = last === null;
        last = text;
        if (first) return; // initial render is already correct server-side
        try { applyVariant(JSON.parse(text)); } catch (e) { /* ignore malformed */ }
      };
      new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(check); })
        .observe(info, { childList: true, subtree: true, characterData: true });
      check();
    });
    // Dawn declares PUB_SUB_EVENTS with a top-level const (global scope, not a window property).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => applyVariant(event && event.data && event.data.variant));
    }
  });

  // ---------- 2. sticky buy bar ----------
  class AjStickyBuy extends HTMLElement {
    connectedCallback() {
      const section = this.dataset.section;
      this.mainBtn = document.getElementById(`ProductSubmitButton-${section}`);
      if (!this.mainBtn) return;
      this.btn = this.querySelector('[data-aj-sticky-btn]');
      this.priceOut = this.querySelector('[data-aj-sticky-price]');
      this.errorOut = this.querySelector('[data-aj-sticky-error]');
      this.removeAttribute('hidden');

      const mobile = window.matchMedia('(max-width: 749px)');
      const footer = document.querySelector('.footer');
      const overlayOpen = () => /overflow-hidden/.test(document.body.className);
      let raf = 0;
      const update = () => {
        raf = 0;
        const main = document.getElementById(`ProductSubmitButton-${section}`) || this.mainBtn;
        const passed = main.getBoundingClientRect().bottom < 0;
        const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        const show = mobile.matches && passed && !footerIn && !overlayOpen();
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('ax-sticky-on', show);
        if (show) document.body.style.setProperty('--ax-sticky-h', `${this.offsetHeight}px`);
      };
      const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('pageshow', schedule);
      mobile.addEventListener?.('change', schedule);
      new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      schedule();

      const sync = () => {
        const main = document.getElementById(`ProductSubmitButton-${section}`);
        if (!main) return;
        const busy = main.getAttribute('aria-disabled') === 'true';
        if (this.btn.disabled !== main.disabled) this.btn.disabled = main.disabled;
        if (busy) this.btn.setAttribute('aria-disabled', 'true'); else this.btn.removeAttribute('aria-disabled');
        this.btn.classList.toggle('is-busy', busy);
        const label = main.querySelector('span');
        const text = label && label.textContent.trim();
        if (text && this.btn.textContent.trim() !== text) this.btn.textContent = text;
        const priceEl = document.getElementById(`price-${section}`);
        const current = priceEl && priceEl.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular');
        const p = current && current.textContent.trim();
        if (p && this.priceOut && this.priceOut.textContent.trim() !== p) this.priceOut.textContent = p;
        const err = main.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const msg = err && !err.hasAttribute('hidden') ? (err.querySelector('.product-form__error-message')?.textContent || '').trim() : '';
        if (msg && this.clearPending) this.clearPending(); // a failed add never arms focus return
        if (this.errorOut) {
          if (msg) { this.errorOut.textContent = msg; this.errorOut.hidden = false; } else if (!this.errorOut.hidden) { this.errorOut.hidden = true; this.errorOut.textContent = ''; }
        }
        schedule();
      };
      const form = this.mainBtn.closest('product-form') || this.mainBtn.closest('form');
      const watch = [document.getElementById(`price-${section}`), form].filter(Boolean);
      const mo = new MutationObserver(sync);
      watch.forEach((t) => mo.observe(t, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden'] }));
      // Focus return is tied to a cart drawer actually opened by THIS bar's submission: armed on click,
      // confirmed when the drawer opens, cleared on failure or timeout, spent when that drawer closes.
      let pending = 0;
      this.returnFocus = false;
      this.clearPending = () => { pending = 0; };
      this.btn.addEventListener('click', (e) => {
        if (this.btn.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        pending = Date.now();
      });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        new MutationObserver(() => {
          const active = drawer.classList.contains('active');
          if (active && pending && Date.now() - pending < 15000) { this.returnFocus = true; pending = 0; }
          if (!active && this.returnFocus) {
            this.returnFocus = false;
            // Dawn removes the overlay class after its own restore attempt; wait for it, then refocus.
            requestAnimationFrame(() => requestAnimationFrame(() => { update(); if (this.classList.contains('is-visible')) this.btn.focus({ preventScroll: true }); }));
          }
        }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
      sync();
    }
  }
  if (!customElements.get('aj-sticky-buy')) customElements.define('aj-sticky-buy', AjStickyBuy);

  // ---------- 3. enquiry context ----------
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-ax-enquiry]');
    if (!link) return;
    const url = new URL(link.getAttribute('href'), window.location.origin);
    const info = link.closest('product-info') || document;
    const options = [...info.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select')]
      .map((el) => (el.tagName === 'SELECT' ? el.options[el.selectedIndex]?.text : el.value))
      .filter(Boolean).join(' / ');
    url.searchParams.set('product', link.dataset.product || '');
    if (options) url.searchParams.set('option', options); else url.searchParams.delete('option');
    link.setAttribute('href', url.pathname + url.search + '#fit-guide');
  });

  onReady(() => {
    const params = new URLSearchParams(window.location.search);
    const product = params.get('product');
    const body = document.querySelector('#ContactForm-body, textarea[name="contact[body]"]');
    if (!product || !body || body.value) return;
    const option = params.get('option');
    const ja = document.documentElement.lang !== 'en';
    body.value = ja
      ? `【お問い合わせ商品】${product}${option ? `（${option}）` : ''}\n車種・年式：\n現在の仕様：\n`
      : `Product: ${product}${option ? ` (${option})` : ''}\nCar (model, year):\nCurrently fitted:\n`;
  });
})();
